import {
  AssessmentType,
  GetAssessmentQuery,
  GetGitHubProvisioningQuery,
  useGetAssessmentQuery,
  useGetGitHubProvisioningQuery,
} from '@gql/gql-types';
import { Box, Container, Grid, Stack } from '@mui/material';
import useTimeLeft from '@pages/interview/hooks/use-time-left';
import { useCallback, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useParams } from 'react-router';
import remarkGfm from 'remark-gfm';

import { GithubCodingActions } from './components/github-coding-actions';
import { GithubCodingCard } from './components/github-coding-components';
import { GithubCodingHeader } from './components/github-coding-header';
import { GithubCodingQuickLink } from './components/github-coding-quick-link';
import {
  AssessmentComplete,
  AssessmentErrorLoading,
  AssessmentLoading,
  AssessmentNotFound,
} from './components/github-coding-state-views';
import { GithubCommits } from './components/github-commits';

export type Assessment = NonNullable<GetAssessmentQuery['assessment']>;
export type GitHubProvisioning = NonNullable<
  GetGitHubProvisioningQuery['gitHubProvisioning']
>;

const GithubCodingPage = () => {
  const { id: assessmentIdFromParams } = useParams();
  const assessmentId = assessmentIdFromParams || 'missing-assessment-id';

  const {
    loading: isAssessmentLoading,
    data: assessmentData,
    error: assessmentError,
    refetch: refetchAssessment,
  } = useGetAssessmentQuery({
    variables: { id: assessmentId },
  });

  const {
    loading: isGitHubProvisioningLoading,
    data: gitHubProvisioningData,
    error: gitHubProvisioningError,
    refetch: refetchGitHubProvisioning,
  } = useGetGitHubProvisioningQuery({
    variables: {
      input: {
        assessmentId,
        sync: true,
      },
    },
  });

  const [isRefetching, setIsRefetching] = useState(false);

  const refetch = useCallback(
    async ({ withGitHubSync = true }: { withGitHubSync?: boolean } = {}) => {
      setIsRefetching(true);
      try {
        await Promise.all([
          refetchAssessment(),
          refetchGitHubProvisioning({
            input: {
              assessmentId,
              sync: withGitHubSync,
            },
          }),
        ]);
      } finally {
        setIsRefetching(false);
      }
    },
    [refetchAssessment, refetchGitHubProvisioning, assessmentId],
  );

  const assessment = assessmentData?.assessment;
  const provisioning = gitHubProvisioningData?.gitHubProvisioning;

  const startedAt = useMemo(
    () => (assessment?.startedAt ? new Date(assessment.startedAt) : null),
    [assessment?.startedAt],
  );
  const duration = useMemo(
    () => assessment?.template.duration ?? null,
    [assessment?.template.duration],
  );
  const timeLeft = useTimeLeft({
    startedAt,
    duration,
  });

  if (isAssessmentLoading || isGitHubProvisioningLoading) {
    return <AssessmentLoading />;
  }

  if (assessmentError || gitHubProvisioningError) {
    return <AssessmentErrorLoading />;
  }

  if (
    !assessment ||
    assessment.template.templateType !== AssessmentType.GithubCoding
  ) {
    return <AssessmentNotFound />;
  }

  if (assessment.completedAt || timeLeft === 0) {
    return <AssessmentComplete assessmentId={assessment.id} />;
  }

  return (
    <Box>
      <GithubCodingHeader assessment={assessment} provisioning={provisioning} />
      <Container sx={{ my: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Stack direction='column' spacing={2}>
              <GithubCodingQuickLink
                assessment={assessment}
                provisioning={provisioning}
              />
              <GithubCodingActions
                assessment={assessment}
                provisioning={provisioning}
                refetch={refetch}
                isRefetching={isRefetching}
              />
              <GithubCommits provisioning={provisioning} refetch={refetch} />
            </Stack>
          </Grid>
          <Grid item xs={12} md={8}>
            <GithubCodingCard>
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {assessment.template.filteredAttributes.taskDescription}
              </ReactMarkdown>
            </GithubCodingCard>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default GithubCodingPage;
